import { useEffect } from "react";
import svgPaths from "./svg-paths";
import "./Affiliates.css";

const APPLY_EMAIL = "hello@evograding.com";
const TERMS_URL = "/affiliate-program-terms";

const bountyRows = [
  {
    tier: "Plus Monthly",
    price: "$11.99",
    payout: "$6 first payment + $4 first renewal bonus",
  },
  {
    tier: "Pro Monthly",
    price: "$19.99",
    payout: "$9 first payment + $6 first renewal bonus",
  },
  {
    tier: "Plus Yearly",
    price: "$99.99",
    payout: "$30",
  },
  {
    tier: "Pro Yearly",
    price: "$169.99",
    payout: "$50",
  },
];

const processSteps = [
  {
    label: "Apply",
    text: "Tell us where you create and what email you use for Evo.",
  },
  {
    label: "Share",
    text: "You get a code. Collectors enter it in Evo before subscribing.",
  },
  {
    label: "Earn",
    text: "If they become a paid subscriber, the fixed bounty is attributed to you.",
  },
];

const proofPoints = [
  "Custom centering and damage models",
  "Built from cracked PSA slab data",
  "Profit view for raw vs graded cards",
  "Free Evo Pro while active",
];

function AffiliateFooter() {
  return (
    <footer className="affiliate-site-footer">
      <div className="affiliate-site-footer-row">
        <div className="affiliate-site-footer-left">
          <a className="affiliate-site-footer-logo" href="/" aria-label="Evo home">
            <svg fill="none" preserveAspectRatio="none" viewBox="0 0 105.6 30">
              <path d={svgPaths.p17c40c00} fill="white" />
              <path d={svgPaths.p3f0f4200} fill="white" />
              <path d={svgPaths.p2e034c00} fill="white" />
              <path d={svgPaths.p1e156e00} fill="white" />
              <path d={svgPaths.p15637080} fill="white" />
            </svg>
          </a>
          <a
            className="affiliate-site-footer-pill"
            href="https://discord.gg/XTxUgUAFJh"
            rel="noreferrer"
            target="_blank"
          >
            <svg fill="none" preserveAspectRatio="none" viewBox="0 0 18 13.9338">
              <path d={svgPaths.pd84a880} fill="white" />
            </svg>
            <span>Join our Discord server</span>
          </a>
          <a className="affiliate-site-footer-pill" href="mailto:hello@evograding.com">
            <svg fill="none" preserveAspectRatio="none" viewBox="0 0 18 18">
              <path
                d={svgPaths.p3ae5cc00}
                stroke="white"
                strokeLinejoin="round"
                strokeWidth="1.5"
              />
              <path
                d={svgPaths.p3e25a540}
                stroke="white"
                strokeLinejoin="round"
                strokeWidth="1.5"
              />
            </svg>
            <span>hello@evograding.com</span>
          </a>
        </div>
        <div className="affiliate-site-footer-right">
          <nav className="affiliate-site-footer-links" aria-label="Footer">
            <a href="/contact">Contact</a>
            <a href="/support">Support</a>
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
          </nav>
          <p>&copy; 2026 Evo Collectibles Inc.</p>
        </div>
      </div>
    </footer>
  );
}

const termsSections = [
  {
    title: "1. Program Overview",
    body: [
      "The Evo Affiliate Program lets approved affiliates promote Evo and earn commissions from eligible new paying subscribers who use the affiliate's code or are otherwise attributed by Evo under these terms.",
      "You are not required to post on any specific schedule. Either you or Evo may end your participation in the Program at any time.",
    ],
  },
  {
    title: "2. Free Evo Pro Access",
    body: [
      "While enrolled in the Program, you may receive free Evo Pro access. This is a Program benefit, not payment for any specific post. Free Pro access ends when your participation in the Program ends.",
    ],
  },
  {
    title: "3. Affiliate Code",
    body: [
      "Evo may provide you with a unique affiliate code. Default attribution is code-based. A user is normally attributed to you only if they enter your affiliate code in the Evo app before starting a paid subscription.",
      "Evo may manually correct or assign affiliate attribution when Evo determines that a user should have been attributed to an affiliate. Manual attribution is an administrative correction, not a guaranteed right.",
    ],
  },
  {
    title: "4. Code Attribution Window",
    body: [
      "Once a user enters your affiliate code, the code attribution remains valid for 30 days. If the user starts a paid subscription within 30 days after entering your code, that user is attributed to you.",
      "If the user does not start a paid subscription within 30 days, the attribution expires. No commission is owed unless the user enters a valid affiliate code again before subscribing or Evo manually applies an attribution correction.",
    ],
  },
  {
    title: "5. One Affiliate Per User",
    body: [
      "Each user may be attributed to only one affiliate. Once a user starts a paid subscription, their affiliate attribution is generally locked and cannot be changed by the user.",
      "Retroactive attribution may only be applied from the user's first paid purchase event. If the user's first purchase is manually attributed, eligible commissions may include that first purchase and, for monthly subscriptions only, the first monthly renewal bonus if that renewal has already occurred or later occurs.",
    ],
  },
  {
    title: "6. Commission Schedule",
    body: [
      "There is no blanket commission rate. Commissions are fixed dollar bounties based on the product purchased. All prices and affiliate payouts are listed in USD.",
      "The monthly renewal bonus applies only to the referred subscriber's first monthly renewal for the same monthly product. No additional monthly renewal commissions are owed after the first renewal bonus. No yearly renewal commissions are owed after the first yearly purchase.",
      "Plus Monthly commissions are capped at $10 per referred customer. Pro Monthly commissions are capped at $15 per referred customer. Plus Yearly commissions are capped at $30 per referred customer. Pro Yearly commissions are capped at $50 per referred customer.",
    ],
  },
  {
    title: "7. Fixed Amounts and Adjustments",
    body: [
      "The commissions listed above are fixed dollar amounts. They are not calculated from a blanket percentage, subscription list price, net revenue, estimated platform fee, payment processing fee, or app store fee.",
      "If a transaction is refunded, reversed, charged back, adjusted, discounted to zero, found fraudulent, or otherwise determined ineligible, the related commission may be reduced, canceled, reversed, or deducted from future payouts.",
    ],
  },
  {
    title: "8. Eligible Users",
    body: [
      "Only new paying subscribers are eligible for commission. Existing paying subscribers, former paying subscribers who resubscribe, self-referrals, duplicate accounts, fraudulent accounts, refunded payments, chargebacks, reversals, and unauthorized coupon, cashback, or deal-site traffic are not commissionable.",
    ],
  },
  {
    title: "9. Subscription Changes",
    body: [
      "If an attributed user upgrades, downgrades, renews, or changes plans, commissions follow the product-specific schedule above. Monthly renewal bonuses require the same monthly product as the first paid purchase.",
    ],
  },
  {
    title: "10. Payout Terms",
    body: [
      "Commissions are calculated monthly and paid on a net-30 basis. Approved commissions for a calendar month are paid within 30 days after the end of that month, provided the commission is valid, payable, and not subject to refund, chargeback, fraud review, tax review, or payment-provider delay.",
      "Evo may set a minimum payout threshold. Evo's records of affiliate codes, attributed users, subscription transactions, payment events, refunds, reversals, chargebacks, commissions, adjustments, and payouts control absent manifest error.",
    ],
  },
  {
    title: "11. Dashboard Estimates and Final Payout Calculation",
    body: [
      "Evo may provide a dashboard or use third-party systems to show estimated referral, conversion, commission, or payout information. These are provided for convenience only and may be delayed, incomplete, inaccurate, or different from the final approved payout amount.",
      "If there is a discrepancy between Evo's ledger, a dashboard, a third-party payout system, and the final payout calculation, Evo's ledger and final payout calculation control absent manifest error.",
    ],
  },
  {
    title: "12. Tax and Payment Information",
    body: [
      "Evo may require tax and payment information before payout. US affiliates must provide a completed Form W-9. Non-US affiliates must provide the applicable Form W-8 or other required tax documentation.",
    ],
  },
  {
    title: "13. Promotion Rules",
    body: [
      "You must clearly disclose your paid relationship with Evo whenever you promote Evo. Your disclosure must be clear, hard to miss, and included in each post, video, stream, story, caption, description, or other promotion where you mention Evo.",
      "You agree not to make false or misleading claims about Evo, claim Evo guarantees a card grade, imply endorsement by PSA, CGC, Beckett, TAG, Pokemon, Nintendo, or TCGplayer, run paid ads using Evo brand terms without written approval, post your code on coupon or deal sites without written approval, create fake signups, or manipulate marketplace reviews.",
    ],
  },
  {
    title: "14. Ending Participation",
    body: [
      "Evo may approve, reject, suspend, or terminate any affiliate's participation in the Program at any time, for any reason or no reason, with or without notice, to the maximum extent permitted by law.",
      "Valid commissions earned before termination remain payable, subject to these terms, unless they are connected to fraud, refunds, chargebacks, reversals, policy violations, misleading promotion, or other Program abuse.",
    ],
  },
  {
    title: "15. Changes to the Program",
    body: [
      "Evo may update these terms, commission amounts, attribution rules, payout rules, eligible products, or other Program terms. Changes apply going forward only unless required for fraud prevention, legal compliance, or correction of an error.",
    ],
  },
  {
    title: "16. Independent Contractor",
    body: [
      "You are an independent contractor. You are not an employee, agent, partner, franchisee, or legal representative of Evo. You are responsible for your own taxes, expenses, content, audience, platform compliance, and legal obligations.",
    ],
  },
  {
    title: "17. Confidentiality",
    body: [
      "Your commission data, referral data, payout data, conversion data, dashboard data, and any non-public Program information are confidential. You may not publicly share this information without Evo's written approval, except with your legal, tax, or financial advisors.",
    ],
  },
  {
    title: "18. Limitation of Liability",
    body: [
      "To the maximum extent allowed by law, Evo is not liable for indirect, incidental, special, consequential, punitive, or lost-profit damages related to the Program. Evo's total liability is limited to unpaid commissions properly owed for the three months before the claim arose.",
    ],
  },
  {
    title: "19. Governing Law",
    body: [
      "These terms are governed by the laws of the State of Delaware, without regard to conflict-of-law rules. Any dispute will be brought in the state or federal courts located in Delaware, unless Evo chooses another venue to seek urgent relief.",
    ],
  },
  {
    title: "20. Viewing Terms",
    body: [
      "By viewing these terms, joining the Program, using your affiliate code, or accepting Program benefits, you confirm that you have been shown the current Program terms.",
    ],
  },
];

function applyHref() {
  const subject = encodeURIComponent("Evo Affiliate Program");
  const body = encodeURIComponent(
    "I'd like to apply for the Evo Affiliate Program.\n\nMy Evo email:\nMy creator handle/link:\nMain platform:\n",
  );
  return `mailto:${APPLY_EMAIL}?subject=${subject}&body=${body}`;
}

export default function Affiliates() {
  useEffect(() => {
    document.title = "Evo Affiliate Program";
  }, []);

  return (
    <main className="affiliates-page">
      <header className="affiliates-nav">
        <a className="affiliates-logo" href="/" aria-label="Evo home">
          <img src="/evo-logo-full.svg" alt="Evo" />
        </a>
        <nav>
          <a href="#payouts">Payouts</a>
          <a href={TERMS_URL}>Terms</a>
          <a className="affiliates-nav-cta" href={applyHref()}>
            Apply
          </a>
        </nav>
      </header>

      <section className="affiliates-hero">
        <div className="affiliates-hero-media" aria-hidden="true">
          <img className="phone phone-back" src="/affiliate-plays.png" alt="" />
          <img className="phone phone-front" src="/affiliate-gradecard.png" alt="" />
        </div>
        <div className="affiliates-hero-content">
          <p className="eyebrow">Creator partnerships</p>
          <h1>Evo Creator Program</h1>
          <p className="hero-copy">
            Earn up to $50 per referred subscriber. Share Evo with collectors
            who want better grading decisions.
          </p>
          <div className="hero-actions">
            <a className="primary-cta" href={applyHref()}>
              Apply by email
            </a>
            <a className="secondary-cta" href={TERMS_URL}>
              Read terms
            </a>
          </div>
        </div>
      </section>

      <section className="affiliate-proof-band" aria-label="Program highlights">
        {proofPoints.map((point) => (
          <div key={point}>{point}</div>
        ))}
      </section>

      <section className="affiliate-section affiliate-intro">
        <div>
          <p className="eyebrow">Why Evo</p>
          <h2>Built from real grading outcomes, not generic image scoring.</h2>
        </div>
        <p>
          Evo was trained from thousands of dollars worth of cracked PSA slabs,
          scanned card images, and custom grading data. The app looks at
          centering, visible damage, grade odds, and raw-vs-graded profit so
          collectors can make better grading decisions before they submit.
        </p>
      </section>

      <section className="affiliate-section process-section">
        <div className="section-heading">
          <p className="eyebrow">How it works</p>
          <h2>Simple attribution, tracked in Tolt.</h2>
        </div>
        <div className="process-grid">
          {processSteps.map((step, index) => (
            <article className="process-card" key={step.label}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{step.label}</h3>
              <p>{step.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="affiliate-section payout-section" id="payouts">
        <div className="section-heading">
          <p className="eyebrow">Fixed bounty schedule</p>
          <h2>No percentage math. No net-fee guessing.</h2>
          <p>
            The backend uses fixed bounties by subscription event. The price
            column is shown only so creators can see the in-app line item.
          </p>
        </div>
        <div className="payout-table-wrap">
          <table className="payout-table">
            <thead>
              <tr>
                <th>Tier</th>
                <th>Price before fees</th>
                <th>Affiliate payout</th>
              </tr>
            </thead>
            <tbody>
              {bountyRows.map((row) => (
                <tr key={row.tier}>
                  <td>{row.tier}</td>
                  <td>{row.price}</td>
                  <td>{row.payout}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="affiliate-section details-grid">
        <article>
          <h2>Attribution</h2>
          <p>
            Users normally need to enter your affiliate code in Evo before
            subscribing. The attribution window is 30 days, and one user can
            only be attributed to one affiliate.
          </p>
        </article>
        <article>
          <h2>Payouts</h2>
          <p>
            Commissions are tracked in Tolt and paid on a net-30 basis after
            review for refunds, chargebacks, reversals, fraud, and other
            non-commissionable activity.
          </p>
        </article>
        <article>
          <h2>Creator access</h2>
          <p>
            Approved affiliates may receive free Evo Pro access while enrolled
            in the program, so you can test the product before sharing it.
          </p>
        </article>
      </section>

      <section className="affiliate-terms" id="terms">
        <div className="terms-heading">
          <p className="eyebrow">Program terms</p>
          <h2>Terms at a glance</h2>
          <p>Full terms are available at the stable program terms URL.</p>
        </div>
        <div className="terms-summary-grid">
          <article>
            <h3>Fixed bounties</h3>
            <p>Commissions are fixed USD amounts by subscription event, not a blanket percentage.</p>
          </article>
          <article>
            <h3>30-day attribution</h3>
            <p>Codes attribute new subscribers for 30 days, with one affiliate per user.</p>
          </article>
          <article>
            <h3>Net-30 payout review</h3>
            <p>Payouts are reviewed for refunds, reversals, chargebacks, fraud, and eligibility.</p>
          </article>
          <article>
            <h3>Read the full terms</h3>
            <p>
              The complete Evo Affiliate Program Terms are published at{" "}
              <a href={TERMS_URL}>evograding.com{TERMS_URL}</a>.
            </p>
          </article>
        </div>
      </section>

      <section className="affiliate-final-cta">
        <p className="eyebrow">Ready to partner?</p>
        <h2>Tell us where you create.</h2>
        <p>
          Send your Evo email and creator profile. We will review the fit and
          set up Tolt if approved.
        </p>
        <a className="primary-cta" href={applyHref()}>
          Apply by email
        </a>
      </section>
      <AffiliateFooter />
    </main>
  );
}

export function AffiliateProgramTerms() {
  useEffect(() => {
    document.title = "Evo Affiliate Program Terms";
  }, []);

  return (
    <main className="affiliates-page affiliate-terms-page">
      <header className="affiliates-nav">
        <a className="affiliates-logo" href="/" aria-label="Evo home">
          <img src="/evo-logo-full.svg" alt="Evo" />
        </a>
        <nav>
          <a href="/affiliates">Program</a>
          <a href="#terms">Terms</a>
          <a className="affiliates-nav-cta" href={applyHref()}>
            Apply
          </a>
        </nav>
      </header>

      <section className="affiliate-terms full-terms" id="terms">
        <div className="terms-heading">
          <p className="eyebrow">Program terms</p>
          <h1>Evo Affiliate Program Terms</h1>
          <p>Last updated: July 7, 2026</p>
        </div>
        <div className="terms-document">
          <p>
            By participating in the Evo Affiliate Program, you acknowledge these
            terms with Evo Collectibles Inc., a Delaware corporation.
          </p>
          <div className="terms-table">
            <table className="payout-table compact">
              <thead>
                <tr>
                  <th>Tier</th>
                  <th>Price before fees</th>
                  <th>Affiliate payout</th>
                </tr>
              </thead>
              <tbody>
                {bountyRows.map((row) => (
                  <tr key={row.tier}>
                    <td>{row.tier}</td>
                    <td>{row.price}</td>
                    <td>{row.payout}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {termsSections.map((section) => (
            <section className="terms-section" key={section.title}>
              <h3>{section.title}</h3>
              {section.body.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </section>
          ))}
        </div>
      </section>
      <AffiliateFooter />
    </main>
  );
}
